import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/safeClient';
import { RealtimeChannel } from '@supabase/supabase-js';
import { format } from 'date-fns';

/**
 * Safely get the Notification API if available.
 * Returns undefined on environments where Notification is not supported (iOS Safari, some browsers).
 */
function getNotificationAPI(): typeof Notification | undefined {
  try {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return window.Notification;
    }
  } catch {
    // Some browsers throw when accessing Notification in certain contexts
  }
  return undefined;
}

export function useNotifications() {
  const { user } = useAuth();
  const checkIntervalRef = useRef<ReturnType<typeof setTimeout>>();
  const channelRef = useRef<RealtimeChannel>();

  useEffect(() => {
    if (!user) return;

    const NotificationAPI = getNotificationAPI();
    
    // If Notification API is not available, skip all notification setup
    if (!NotificationAPI) {
      if (import.meta.env.DEV) {
      }
      return;
    }

    // Request notification permission
    requestNotificationPermission();

    // Check for upcoming meetups every minute
    checkUpcomingMeetups();
    checkIntervalRef.current = setInterval(checkUpcomingMeetups, 60000);

    // Subscribe to deal updates
    subscribeToDeals();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user]);

  const requestNotificationPermission = async () => {
    const NotificationAPI = getNotificationAPI();
    if (!NotificationAPI) return;

    try {
      if (NotificationAPI.permission === 'default') {
        await NotificationAPI.requestPermission();
      }
    } catch (error) {
      // Safari can throw on requestPermission in some contexts
      if (import.meta.env.DEV) {
      }
    }
  };

  const checkUpcomingMeetups = async () => {
    const NotificationAPI = getNotificationAPI();
    if (!user || !NotificationAPI || NotificationAPI.permission !== 'granted') return;

    try {
      const now = new Date();
      
      // Check for 15 minutes BEFORE meetup
      const fifteenMinutesBefore = new Date(now.getTime() + 15 * 60000);
      const sixteenMinutesBefore = new Date(now.getTime() + 16 * 60000);
      
      // Check for 15 minutes AFTER meetup (for late arrivals)
      const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60000);
      const fourteenMinutesAgo = new Date(now.getTime() - 14 * 60000);

      // Query for deals with notify_30min_before enabled (using it as general notification toggle)
      const { data: upcomingDeals, error: upcomingError } = await supabase
        .from('deals')
        .select(`
          *,
          listing:listings(title),
          buyer:profiles!deals_buyer_id_fkey(name, username),
          seller:profiles!deals_seller_id_fkey(name, username)
        `)
        .eq('status', 'active')
        .eq('notify_30min_before', true)
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .gte('meetup_time', fifteenMinutesBefore.toISOString())
        .lte('meetup_time', sixteenMinutesBefore.toISOString());

      if (!upcomingError && upcomingDeals) {
        upcomingDeals.forEach((deal) => {
          const otherParty = deal.buyer_id === user.id ? deal.seller : deal.buyer;
          const meetupTime = format(new Date(deal.meetup_time), 'h:mm a');
          
          showNotification(
            '⏰ 15 min until meetup!',
            `Your meetup with ${otherParty?.username || otherParty?.name || 'the other party'} for "${deal.listing?.title || 'deal'}" is in 15 minutes. Location: ${deal.meetup_location_address}`,
            '/near-me'
          );
        });
      }

      // Query for deals 15 minutes AFTER meetup time (reminder for late situation)
      const { data: overdueDeals, error: overdueError } = await supabase
        .from('deals')
        .select(`
          *,
          listing:listings(title),
          buyer:profiles!deals_buyer_id_fkey(name, username),
          seller:profiles!deals_seller_id_fkey(name, username)
        `)
        .eq('status', 'active')
        .eq('notify_30min_before', true)
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .gte('meetup_time', fifteenMinutesAgo.toISOString())
        .lte('meetup_time', fourteenMinutesAgo.toISOString());

      if (!overdueError && overdueDeals) {
        overdueDeals.forEach((deal) => {
          const otherParty = deal.buyer_id === user.id ? deal.seller : deal.buyer;
          
          showNotification(
            '⚠️ 15 min past meetup time',
            `Your meetup with ${otherParty?.username || otherParty?.name || 'the other party'} for "${deal.listing?.title || 'deal'}" was 15 minutes ago. Please check the situation.`,
            '/near-me'
          );
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[Notifications] Error checking upcoming meetups:', error);
      }
    }
  };

  const subscribeToDeals = () => {
    const NotificationAPI = getNotificationAPI();
    if (!user || !NotificationAPI || NotificationAPI.permission !== 'granted') return;

    // Subscribe to buyer deals
    channelRef.current = supabase
      .channel('deal-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deals',
          filter: `buyer_id=eq.${user.id}`,
        },
        handleDealUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deals',
          filter: `seller_id=eq.${user.id}`,
        },
        handleDealUpdate
      )
      .subscribe();
  };

  const handleDealUpdate = async (payload: any) => {
    if (!user) return;
    const updatedDeal = payload.new;
    const oldDeal = payload.old;

    // Fetch related data
    const { data: dealData } = await supabase
      .from('deals')
      .select(`
        listing:listings(title),
        buyer:profiles!deals_buyer_id_fkey(name, username),
        seller:profiles!deals_seller_id_fkey(name, username)
      `)
      .eq('id', updatedDeal.id)
      .single();

    if (!dealData) return;

    const otherParty = updatedDeal.buyer_id === user.id 
      ? dealData.seller 
      : dealData.buyer;

    // Build notification message
    let changeMessage = '';
    const timeChanged = oldDeal.meetup_time !== updatedDeal.meetup_time;
    const locationChanged = oldDeal.meetup_location_address !== updatedDeal.meetup_location_address;

    if (timeChanged && locationChanged) {
      changeMessage = `Time changed to ${format(new Date(updatedDeal.meetup_time), 'MMM d, h:mm a')} and location changed to ${updatedDeal.meetup_location_address}`;
    } else if (timeChanged) {
      changeMessage = `Time changed to ${format(new Date(updatedDeal.meetup_time), 'MMM d, h:mm a')}`;
    } else if (locationChanged) {
      changeMessage = `Location changed to ${updatedDeal.meetup_location_address}`;
    }

    if (changeMessage) {
      showNotification(
        `Deal Updated: ${dealData.listing?.title || 'Item'}`,
        `${otherParty?.username || otherParty?.name || 'Someone'} updated the deal. ${changeMessage}`,
        `/chat`
      );
    }
  };

  const showNotification = (title: string, body: string, url?: string) => {
    const NotificationAPI = getNotificationAPI();
    if (!NotificationAPI || NotificationAPI.permission !== 'granted') return;

    try {
      const notification = new NotificationAPI(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'gem-notification',
        requireInteraction: true,
      });

      notification.onclick = () => {
        window.focus();
        if (url) {
          window.location.href = url;
        }
        notification.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);
    } catch (error) {
      // Notification constructor can throw in some environments
      if (import.meta.env.DEV) {
      }
    }
  };

  return {
    requestNotificationPermission,
    showNotification,
  };
}
